import type { AggregatedProductDto } from 'types/api/aggregatedProductDto';
import type { BaseResponseDto } from 'types/api/baseResponseDto';
import type { BulkProductCreateResponseDto } from 'types/api/bulkProductCreateResponseDto';
import type { CommonBulkUpdateResponseDto } from 'types/api/commonBulkUpdateResponseDto';
import type { FindingStats } from 'types/api/findingStats';
import type { ListGroupProductListResponse } from 'types/api/listGroupProductlListResponse';
import type { ListProductRequest } from 'types/api/listProductRequest';
import type { NameIdPair } from 'types/api/nameIdPair';
import type { PaginatedRequestParams } from 'types/api/paginatedRequestParams';
import type {
  AppsecToolStatusByProductDTO,
  AppsecToolStatusByProductResponse,
  BulkProductUploadRequestDto,
  BulkProductVersionCreateReqDto,
  CommonCsvExportResultDto,
  DynamicProductFilters,
  DynamicProductRow,
  FindingSlaTemplateParams,
  FindingSlaTierResponseDto,
  GlobalFilterDto,
  GlobalFilterResponseDto,
  HallOfFameParams,
  ProductAggregateionTypeEnum,
  ProductBulkUpdatePayload,
  ProductDependencyListParams,
  ProductFilterTypeEnum,
  ProductGetParams,
  ProductGlobalDto,
  ProductHierarchyFilterOptions,
  ProductRequestDto,
  ProductResponseShort,
  ProductSubProductResponseDto,
  ProductTypeSequenceReqDto,
  ProductValidateParams,
  ProductVersionCreateReqDto,
  ProductsByBUParams,
  ProductsByGroupRequest,
  PropertyUpdateRequestDto,
} from 'types/api/product';
import type { ProductAggregatedResponse } from 'types/api/productAggregatedResponse';
import type { ProductByGroupResponseDto } from 'types/api/productByGroupResponseDto';
import type { ProductEsDto } from 'types/api/productEsDto';
import type { ProductNameIdPageRequest } from 'types/api/productNameIdPageRequest';
import type { ProductReqDto } from 'types/api/productReqDto';
import type { ProductResponseDto } from 'types/api/productResponseDto';
import type { ProductSubProductResponse } from 'types/api/productSubProductResponse';
import type { ProductTypeJpaDto } from 'types/api/productTypeJpaDto';
import type { ProductTypeReqDto } from 'types/api/productTypeReqDto';
import type { ProductTypeUpdateReqDto } from 'types/api/productTypeUpdateReqDto';
import type { ProductUpdateReqDto } from 'types/api/productUpdateReqDto';
import type { SubProductResponseDto } from 'types/api/subProductResponseDto';
import type { PageResponse } from 'types/pageResponse';

import CustomDashboardAPI from './CustomDashboardAPI';
import GlobalSettingAPI from './GlobalSettingAPI';
import APIClient, {
  failSafe,
  makeCancellableAPIRequest,
  showAPIError,
} from './index';

const ProductAPI = {
  getTypes() {
    const { promise, cancel, requestInfo } = makeCancellableAPIRequest<
      ProductTypeJpaDto[]
    >('GET', '/user/product-type');
    return { promise, cancel, requestInfo };
  },
  createType(data: ProductTypeReqDto) {
    return makeCancellableAPIRequest<ProductTypeJpaDto>(
      'POST',
      '/user/product-type',
      {
        data,
      },
    );
  },
  updateType(data: ProductTypeUpdateReqDto) {
    return makeCancellableAPIRequest<ProductTypeJpaDto>(
      'PUT',
      '/user/product-type',
      {
        data,
      },
    );
  },
  deleteType(id: number) {
    return makeCancellableAPIRequest<void>(
      'DELETE',
      `/user/product-type/${id}`,
      {},
      false,
    );
  },
  orderType(data: ProductTypeSequenceReqDto[]) {
    return makeCancellableAPIRequest<ProductTypeJpaDto[]>(
      'POST',
      '/user/product-type/order',
      {
        data,
      },
    );
  },
  async save(data: ProductReqDto) {
    try {
      const response = await APIClient.post<ProductResponseDto>(
        '/user/product',
        {
          ...data,
        },
      );
      return response.data;
    } catch (e) {
      console.log(e);
      showAPIError(e as Parameters<typeof showAPIError>[0]);
      throw e;
    }
  },
  getAll() {
    console.warn(
      "'ProductAPI.getAll' is depricated, use 'ProductAPI.v2.getAll'",
    );
    return failSafe(APIClient.get<ProductResponseDto[]>('/user/product'));
  },
  get(id: number, queryParams: ProductGetParams = {}) {
    return makeCancellableAPIRequest<ProductResponseDto>(
      'GET',
      `/user/product/${id}?ignoreSubProducts=true`,
      { params: queryParams },
    );
  },
  update(data: ProductUpdateReqDto) {
    return failSafe(
      APIClient.put<ProductResponseDto>('/user/product/', {
        ...data,
      }),
    );
  },
  delete(id: number) {
    return failSafe(APIClient.delete<void>(`/user/product/${id}`));
  },
  archiveDelete(id: number, comment: string) {
    return failSafe(
      APIClient.post<void>(
        `/user/product/archive/${id}?comment=${comment}&delete=true`,
      ),
    );
  },
  getProductByBu(businessUnitId: number, id: number) {
    return makeCancellableAPIRequest<ProductResponseDto>(
      'GET',
      `/user/product/${businessUnitId}/${id}`,
    );
  },
  v2: {
    /**
     * GET /user/product/elastic.
     *
     * The controller returns a bare List<ProductEsDto> for interactive users
     * and a Page<ProductEsDto> only for API-token users. Every caller in this
     * app is an interactive user, so the array form is the effective contract.
     */
    getAll(params: ListProductRequest = {}) {
      return makeCancellableAPIRequest<ProductEsDto[]>(
        'GET',
        '/user/product/elastic',
        {
          params,
        },
      );
    },
    get(id: number | string) {
      return makeCancellableAPIRequest<ProductEsDto>(
        'GET',
        `/user/product/elastic/${id}`,
      );
    },
    getPaginatedAll(data: ListProductRequest) {
      return makeCancellableAPIRequest<PageResponse<ProductEsDto>>(
        'POST',
        '/user/product/elastic/paged',
        {
          data,
        },
      );
    },
    // ENG-131800: paginated + searchable product name/id lookup for dropdown optimisation.
    // Backend: ENG-131798, POST /api/v2/product/name-id -> BaseResponseDto<Page<NameIdDto>>.
    // `ids` hydrates preselected values not present in the current page.
    getNameIdPaged(data: ProductNameIdPageRequest = {}) {
      return makeCancellableAPIRequest<
        BaseResponseDto<PageResponse<NameIdPair>>
      >('POST', '/api/v2/product/name-id', {
        data,
      });
    },
  },
  getFilter() {
    return failSafe(APIClient.get<ProductGlobalDto[]>('/user/product/filter'));
  },
  postFilter(data: GlobalFilterDto) {
    return makeCancellableAPIRequest<void>('POST', '/user/product/filter', {
      data,
    });
  },
  getGlobalFilter() {
    return failSafe(
      APIClient.get<GlobalFilterResponseDto>('/user/product/global/filter/'),
    );
  },
  getAllQuick(BUAggregation = false) {
    const path = BUAggregation
      ? '/api/businessunits/dashboard/product/elastic/short'
      : '/user/product/elastic/short';
    return makeCancellableAPIRequest<NameIdPair[]>('GET', path);
  },
  getAllQuick_V2(data: ListProductRequest = {}) {
    return makeCancellableAPIRequest<BaseResponseDto<ProductResponseShort[]>>(
      'POST',
      'api/product/short',
      { data, cache: true },
    );
  },
  getAllProductsSpecificFields(
    fields: string[] = [],
    filters?: DynamicProductFilters,
    buIds?: number[],
  ) {
    if (!fields.includes('id')) {
      fields.push('id');
    }
    if (!fields.includes('name')) {
      fields.push('name');
    }
    return makeCancellableAPIRequest<DynamicProductRow[]>(
      'POST',
      '/user/product/dynamic',
      {
        data: { fields, filters, buIds },
      },
    );
  },
  getByBU(buId: number) {
    return makeCancellableAPIRequest<NameIdPair[]>(
      'GET',
      `/api/tenant/businessUnits/${buId}/products`,
    );
  },
  getAllProductsByBU(buId: number, params: ProductsByBUParams = {}) {
    const finalParams: ProductsByBUParams = {
      fetchTags: false,
      ...params,
    };
    return makeCancellableAPIRequest<ProductSubProductResponse[]>(
      'GET',
      `/user/product/elastic/business-unit/${buId}`,
      {
        params: finalParams,
      },
    );
  },
  getFilteredProductsByBU(buId: number) {
    return makeCancellableAPIRequest<ProductSubProductResponseDto[]>(
      'GET',
      `/user/product/elastic/business-unit/filtered/${buId}`,
    );
  },
  validate(
    params: ProductValidateParams | Record<string, never> = {},
    showDefaultMessage = false,
  ) {
    return makeCancellableAPIRequest<string>(
      'GET',
      '/user/product/validate',
      {
        params,
      },
      showDefaultMessage,
    );
  },
  getHallOfFame(params: HallOfFameParams) {
    return makeCancellableAPIRequest<FindingStats>(
      'GET',
      '/user/product/elastic/products/hall-of-fame/',
      {
        params,
      },
    );
  },
  getHallOfFameByTeam(params: HallOfFameParams) {
    return makeCancellableAPIRequest<FindingStats>(
      'GET',
      '/user/product/elastic/products/hall-of-fame/by-team/',
      {
        params,
      },
    );
  },
  getAppSecStatus(params: { environmentName?: string[] }) {
    return makeCancellableAPIRequest<AppsecToolStatusByProductResponse[]>(
      'GET',
      '/user/tools/appsec-tools/status-by-product',
      {
        params,
      },
    );
  },
  getAppSecStatusv2(
    restParams: Omit<AppsecToolStatusByProductDTO, 'aggregateField'>,
  ) {
    return makeCancellableAPIRequest<AppsecToolStatusByProductResponse[]>(
      'POST',
      '/user/tools/appsec-tools/status-aggregated-by',
      {
        data: {
          aggregateField: 'PRODUCT',
          ...restParams,
        },
      },
    );
  },
  getFindingSLADetails(params: FindingSlaTemplateParams) {
    return makeCancellableAPIRequest<FindingSlaTierResponseDto>(
      'GET',
      'user/findingSla/sla-template',
      {
        params,
      },
    );
  },
  getAggregatedByTeam() {
    return makeCancellableAPIRequest<ProductAggregatedResponse[]>(
      'GET',
      '/user/product/get-aggreagted-data-by-team',
    );
  },
  getAggregatedByTeamNew() {
    return makeCancellableAPIRequest<AggregatedProductDto[]>(
      'GET',
      '/user/product/get-aggreagted-data?aggType=TEAM',
    );
  },
  getAggregatedByTier() {
    return makeCancellableAPIRequest<AggregatedProductDto[]>(
      'GET',
      '/user/product/get-aggreagted-data?aggType=TIER',
    );
  },
  /**
   * The response is SubProductResponseDto[]. Consumers that render a narrowed
   * projection of these rows may supply a stricter `T`.
   */
  getProductDependencyList<T = SubProductResponseDto[]>(
    id: number,
    params: ProductDependencyListParams,
  ) {
    return makeCancellableAPIRequest<T>(
      'GET',
      '/user/product/get-mapping/' + id,
      {
        params,
      },
    );
  },
  deleteProductDependency(data: { productId: number; subProductId: number }) {
    const { productId, subProductId } = data;
    return failSafe(
      APIClient.delete<void>(
        `/user/product/remove-mapping/${productId}/${subProductId}`,
      ),
    );
  },
  updateProductDependency(data: {
    productId: number;
    subProductId: number;
    considerRiskScore: boolean;
  }) {
    const { productId, subProductId, considerRiskScore } = data;
    return makeCancellableAPIRequest<void>(
      'PUT',
      `/user/product/add-mapping/${productId}/${subProductId}/${considerRiskScore}`,
    );
  },
  importCSV(data: FormData, isEdit?: boolean) {
    return makeCancellableAPIRequest<BulkProductCreateResponseDto>(
      isEdit ? 'PUT' : 'POST',
      '/user/product/upload',
      {
        data,
        headers: {
          'content-type': 'multipart/form-data',
        },
      },
    );
  },
  moveToAnotherBusinessUnit(productId: number, newBuId: number) {
    return makeCancellableAPIRequest<void>(
      'POST',
      `/user/product/${productId}/move/${newBuId}`,
    );
  },
  archiveProduct(productId: number, note: string) {
    return makeCancellableAPIRequest<void>(
      'POST',
      `/user/product/archive/${productId}?comment=${note}`,
    );
  },
  downloadCsvV2(queryParams: ListProductRequest = {}) {
    return makeCancellableAPIRequest<CommonCsvExportResultDto>(
      'POST',
      '/api/products/export',
      {
        data: queryParams,
      },
    );
  },
  bulkUpdate(ids: number[], payload: ProductBulkUpdatePayload) {
    return makeCancellableAPIRequest<CommonBulkUpdateResponseDto>(
      'PATCH',
      '/user/product/bulk',
      {
        data: {
          ids,
          ...payload,
        },
      },
    );
  },
  bulkMove(ids: number[], buId: number) {
    return makeCancellableAPIRequest<void>('POST', '/api/products/bulk/move', {
      data: {
        productIds: ids,
        targetBusinessUnitId: buId,
      },
    });
  },
  bulkDelete(ids: number[]) {
    return makeCancellableAPIRequest<void>('DELETE', '/api/products/bulk', {
      data: {
        productIds: ids,
      },
    });
  },
  bulkArchive(ids: number[], note?: string, shouldDelete = false) {
    return makeCancellableAPIRequest<void>(
      'POST',
      '/api/products/bulk/archive',
      {
        data: {
          productIds: ids,
          comment: note,
          delete: shouldDelete,
        },
      },
    );
  },
  getProductsByGroup({ buId, groups }: ProductsByGroupRequest) {
    return makeCancellableAPIRequest<
      Record<string, ProductByGroupResponseDto[]>
    >('POST', 'user/sub-product/elastic/products-by-group', {
      data: {
        buId,
        groups,
      },
    });
  },
  getPaginatedProductsByHierarchy(
    params: PaginatedRequestParams,
    data: ProductRequestDto,
  ) {
    return makeCancellableAPIRequest<ListGroupProductListResponse>(
      'POST',
      '/api/v2/product/products-by-group',
      {
        params,
        data,
      },
    );
  },
  createVersion({
    productId,
    version,
    cloneManualFindings,
    status,
  }: ProductVersionCreateReqDto) {
    return makeCancellableAPIRequest<ProductResponseDto>(
      'POST',
      '/api/product/version',
      {
        data: {
          productId,
          version,
          cloneManualFindings,
          status,
        },
      },
    );
  },
  createBulkVersion({
    productIds,
    version,
    cloneManualFindings,
    status,
  }: BulkProductVersionCreateReqDto) {
    return makeCancellableAPIRequest<string>(
      'POST',
      '/api/product/bulk/versions',
      {
        data: {
          productIds,
          version,
          cloneManualFindings,
          status,
        },
      },
    );
  },
  getProductBulkPreviewVersions({
    productIds,
    version,
    cloneManualFindings,
  }: Omit<BulkProductVersionCreateReqDto, 'status'>) {
    return makeCancellableAPIRequest<string[]>(
      'POST',
      '/api/product/bulk/preview/versions',
      {
        data: {
          productIds,
          version,
          cloneManualFindings,
        },
      },
    );
  },
  getTeamsByProduct(productId: number) {
    return makeCancellableAPIRequest<BaseResponseDto<NameIdPair[]>>(
      'GET',
      `/api/team/search/${productId}`,
    );
  },
  updateOtherProperties(productId: number, data: PropertyUpdateRequestDto) {
    return makeCancellableAPIRequest<BaseResponseDto<string>>(
      'PATCH',
      `/api/product/${productId}/properties`,
      { data },
    );
  },
  getDefaultProductSubproductDashboard() {
    const dashboardsRequest = CustomDashboardAPI.getAllPublicDashbards();
    const customizedDefaultRequest =
      GlobalSettingAPI.getCustomizeDefaultDashboards();

    const _promise = Promise.all([
      dashboardsRequest.promise,
      customizedDefaultRequest.promise,
    ]).then(([dashboards, customizedDefaultDashboardsData]) => {
      const findDashboard = (dashboardId?: number) => {
        if (!dashboardId) return undefined;
        return dashboards.find((dashboard) => dashboard.id === dashboardId);
      };

      const primaryDashboardData = findDashboard(
        customizedDefaultDashboardsData?.primaryDashboard,
      );
      const secondaryDashboardData = findDashboard(
        customizedDefaultDashboardsData?.secondaryDashboard,
      );

      return {
        primaryDashboard: primaryDashboardData && {
          id: primaryDashboardData?.id,
          name: primaryDashboardData?.name,
        },
        secondaryDashboard: secondaryDashboardData && {
          id: secondaryDashboardData?.id,
          name: secondaryDashboardData?.name,
        },
      };
    });

    return {
      cancel: () => {
        dashboardsRequest.cancel();
        customizedDefaultRequest.cancel();
      },
      promise: _promise,
    };
  },
  getHierarchyFilterOptions(fields: ProductFilterTypeEnum[] | string[] = []) {
    return makeCancellableAPIRequest<ProductHierarchyFilterOptions>(
      'POST',
      '/api/v2/product/filter-options',
      {
        data: { fields },
      },
    );
  },
};

export default ProductAPI;
